// Brand config — the ONE place the product name/tagline/logo live.
// Every frontend surface (header, titles, chat labels, plans, footers)
// reads from here, so a rebrand is a one-file change with no visual
// side effects anywhere else.
//
// "Navarch" comes from the Greek "návarchos" — commander of the fleet —
// hence the ship's-helm logo with the gold admiral's star at its center.
// Free beta mode: while false, every payment/upgrade CTA is hidden (header
// Upgrade pill, Pro Plan prompt-bar link, See plans, pricing cards — /plans
// shows a free-beta notice instead so old links keep working). Flip to true
// at launch to bring everything back — no code was removed, only gated.
export const PAYMENTS_VISIBLE = false;

export const BRAND = {
  name: 'NavarchAI',
  // The header wordmark renders as {nameStart}<em>{nameAccent}</em> —
  // "Navarch" in the text color, "AI" in the accent color. Everywhere
  // else (titles, copy) the name is used as the single word BRAND.name.
  nameStart: 'Navarch',
  nameAccent: 'AI',
  tagline: 'Maritime AI Assistant',
  logo: '☸', // text/emoji mark for inline contexts (menus, print headers)
  note: "Navarch — from the Greek 'návarchos', commander of the fleet.",
  colors: {
    navy: '#1A1F36',
    gold: '#C9A227',
  },
};

// Spoke endpoints for the helm: 8 handles every 45°, running from just
// outside the hub (r=4.4) to past the rim (r=14.8) in a 32×32 viewBox.
const SPOKE_INNER_R = 4.4;
const SPOKE_OUTER_R = 14.8;
const SPOKES = Array.from({ length: 8 }, (_, i) => {
  const angle = (i * Math.PI) / 4;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    x1: 16 + SPOKE_INNER_R * cos,
    y1: 16 + SPOKE_INNER_R * sin,
    x2: 16 + SPOKE_OUTER_R * cos,
    y2: 16 + SPOKE_OUTER_R * sin,
  };
});

// Gold admiral's star on the hub: 5-pointed, point-up, small enough to
// degrade gracefully into a dot at favicon sizes.
const STAR_OUTER_R = 2.3;
const STAR_INNER_R = 0.95;
const STAR_POINTS = Array.from({ length: 10 }, (_, k) => {
  const r = k % 2 === 0 ? STAR_OUTER_R : STAR_INNER_R;
  const angle = -Math.PI / 2 + (k * Math.PI) / 5;
  return `${16 + r * Math.cos(angle)},${16 + r * Math.sin(angle)}`;
}).join(' ');

// Ship's-helm logo: plain geometric wheel — rim, 8 spoke-handles, hub with
// the gold admiral's star. No gradients; stroke widths chosen to stay crisp
// at 32px and 20px. Colors are overridable so the mark works on dark
// surfaces (e.g. white on the navy header tile).
export function BrandLogo({
  size = 32,
  // Theme tokens first (set by navarchTheme.js) so the mark adapts to the
  // header tile in both themes; the brand colors remain the fallback.
  color = `var(--logo-stroke, ${BRAND.colors.navy})`,
  accent = `var(--logo-star, ${BRAND.colors.gold})`,
  title = `${BRAND.name} logo`,
}) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" role="img" aria-label={title}>
      <circle cx="16" cy="16" r="9.5" fill="none" stroke={color} strokeWidth="2.4" />
      {SPOKES.map((s, i) => (
        <line
          key={i}
          x1={s.x1}
          y1={s.y1}
          x2={s.x2}
          y2={s.y2}
          stroke={color}
          strokeWidth="2.2"
          strokeLinecap="round"
        />
      ))}
      <circle cx="16" cy="16" r="3.2" fill={color} />
      <polygon points={STAR_POINTS} fill={accent} />
    </svg>
  );
}
